import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dashboardService,
  type DashboardStats,
  type RecentActivity,
  type QuickAction,
  type OnboardingProgress,
} from '@/services/dashboard.service';

interface DashboardQueryOptions {
  enabled?: boolean;
}

/**
 * Query keys for dashboard-related queries
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  activity: (limit?: number) => [...dashboardKeys.all, 'activity', limit] as const,
  quickActions: () => [...dashboardKeys.all, 'quick-actions'] as const,
  onboarding: () => [...dashboardKeys.all, 'onboarding'] as const,
};

/**
 * Hook to fetch dashboard statistics
 */
export function useDashboardStats(options: DashboardQueryOptions = {}) {
  return useQuery<DashboardStats>({
    queryKey: dashboardKeys.stats(),
    queryFn: () => dashboardService.getStats(),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Refresh every 5 minutes
    enabled: options.enabled ?? true,
  });
}

/**
 * Hook to fetch recent activity
 */
export function useRecentActivity(limit = 10, options: DashboardQueryOptions = {}) {
  return useQuery<RecentActivity>({
    queryKey: dashboardKeys.activity(limit),
    queryFn: () => dashboardService.getRecentActivity(limit),
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled: options.enabled ?? true,
  });
}

/**
 * Hook to fetch quick actions
 */
export function useQuickActions(options: DashboardQueryOptions = {}) {
  return useQuery<QuickAction[]>({
    queryKey: dashboardKeys.quickActions(),
    queryFn: () => dashboardService.getQuickActions(),
    staleTime: 1000 * 60 * 10, // 10 minutes - actions don't change often
    enabled: options.enabled ?? true,
  });
}

/**
 * Hook to fetch onboarding checklist progress
 */
export function useOnboardingProgress(options: DashboardQueryOptions = {}) {
  return useQuery<OnboardingProgress>({
    queryKey: dashboardKeys.onboarding(),
    queryFn: () => dashboardService.getOnboardingProgress(),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options.enabled ?? true,
  });
}

/**
 * Hook to dismiss onboarding checklist
 */
export function useDismissOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dashboardService.dismissOnboarding(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.onboarding() });
    },
  });
}
